import { useMemo, useState } from "react";
import { KachelBetrag } from "../../../components/ui";
import { Link } from "react-router";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "../../../components/Toast";
import { addMonths } from "@talentkontor/domain";
import type { StaffWorkspace } from "../../../app/session";
import { vatWord } from "../einstellungen/model";
import { api } from "../../../data";
import { talentName } from "../../../data/queries";
import { EinrichtungOffen, ErrorState, Loading } from "../../../components/States";
import { StartKarte, StartStattUebersicht, useStartSchritte } from "../Startleitfaden";
import { einrichtungsLuecke } from "../../../lib/einrichtung";
import { BarChart, Donut, LineChart, RankBars } from "../../../components/charts";
import { currencyOptionLabel, money, monthLabel, monthName, monthShort, numberLocale, percent, moneyWhole, shortAmount } from "../../../lib/format";
import { agencyTodos, dueLabel } from "../../../model/todos";
import { CONTRACT_HINT_MONTHS, rangeOf, useDashboardData, useExklusivitaet, useIntegrity, useOverview, useSetupStatus, type RangeKey } from "./data";
import { PIPELINE_STAGES } from "@talentkontor/domain";
import { AGING_LABEL, PIPELINE_LABEL, findingHref, findingText, platformName, setupFolgen, statusLabel } from "./labels";
import { MonatsDetails } from "./MonatsDetails";
import { BeitraegeHinweis } from "./BeitraegeHinweis";
import { EmpfehlungHinweis } from "./EmpfehlungHinweis";
import { TarifGrenzenHinweis } from "../../../components/Tarif";
import { t } from "../../../lib/sprache";
import { useAuslassungTitel } from "../../../lib/auslassung";
import "./uebersicht.css";
import { NurSchreiben } from "../../../app/nurLesen";

/**
 * Übersicht der Agentur. Aufbau nach 11_app_agentur/dashboard.html (Revision 4), Umfang wie das bisherige Portal
 * (AD-016 … AD-038, AD-214 … AD-224): Wächter, zehn Kacheln, Monatsumsatz mit sechs Linien und Monatsdetails,
 * Ranglisten, Plattformen, Pipeline, Altersstruktur offener Rechnungen, Umsatzvorschau, Umsatzentwicklung, Performance, Markenwerte.
 */

function Delta({ value, suffix = "" }: { value: number | null; suffix?: string }) {
  if (value === null || !Number.isFinite(value)) return null;
  const cls = value > 0.5 ? "up" : value < -0.5 ? "down" : "flat";
  const sign = value > 0.5 ? "+" : value < -0.5 ? "−" : "±";
  return <span className={`tk-delta ${cls}`}>{sign}{percent(Math.abs(value))}{suffix}</span>;
}

const SERIES_COLORS = ["var(--tk-s1)", "var(--tk-s2)", "var(--tk-s3)", "var(--tk-s4)", "var(--tk-s5)", "var(--tk-s6)"];
const STEP_LINKS: Record<string, { label: string; to: string }> = {
  company: { get label() { return t("Firmendaten und Rechtsangaben", "Company details and legal information"); }, to: "/agentur/einstellungen/firma" },
  bank: { get label() { return t("Bankverbindung für Rechnungen", "Bank details for invoices"); }, to: "/agentur/einstellungen/firma" },
  branding: { get label() { return t("Absender, Antwortadresse und Sprache", "Sender, reply address and language"); }, to: "/agentur/einstellungen/mailversand" },
  terms: { get label() { return t("Provision und Zahlungsziele", "Commission and payment terms"); }, to: "/agentur/einstellungen/provision" },
  dunning: { get label() { return t("Mahnleiter", "Dunning levels"); }, to: "/agentur/einstellungen/mahnleiter" },
  invoicing: { get label() { return t("Rechnungsnummern", "Invoice numbers"); }, to: "/agentur/einstellungen/nummernkreise" },
};

export function ChefUebersicht({ ws }: { ws: StaffWorkspace }) {
  const qc = useQueryClient();
  const { q, error, loading, today, views, retry } = useDashboardData(ws);
  const [currency, setCurrency] = useState(ws.org.currency);
  const [rangeKey, setRangeKey] = useState<RangeKey>("6m");
  const [custom, setCustom] = useState<{ from: string; to: string }>({ from: addMonths(today.slice(0, 7), -5), to: today.slice(0, 7) });
  const [talentId, setTalentId] = useState("");
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [month, setMonth] = useState<string | null>(null);
  const setup = useSetupStatus(ws.org.org_id, ws.isChef);
  const start = useStartSchritte(ws);

  const r = rangeOf(rangeKey, today, custom);
  const overview = useOverview(q, { today, from: r.from, to: r.to, talentId, currency });
  const integrity = useIntegrity(q, today, overview, talentId || null);
  const exklusiv = useExklusivitaet(q, talentId || null);
  const [vertraegeOffen, setVertraegeOffen] = useState(false);
  // Kompakt (D-1, Inhaber 21.10.2026): Aufgaben oben als Liste, ab zwölf Einträgen der Rest auf Klick
  const [alleAufgaben, setAlleAufgaben] = useState(false);
  const kompakt = useAuslassungTitel<HTMLElement>();

  const names = useMemo(() => {
    const talents = new Map((q.talents.data ?? []).map((t) => [t.id, t]));
    const brands = new Map((q.brands.data ?? []).map((b) => [b.id, b]));
    const deals = new Map((q.deals.data ?? []).map((d) => [d.id, d]));
    return {
      currency: (dealId: string | null) => (dealId ? deals.get(dealId)?.currency ?? null : null),
      talent: (id: string | null) => (id ? talentName(talents.get(id)) : "–"),
      brand: (id: string | null) => (id ? brands.get(id)?.name ?? "–" : "–"),
      deal: (id: string) => {
        const d = deals.get(id);
        return d ? `${brands.get(d.brand_id)?.name ?? "–"} · ${d.title}` : "–";
      },
    };
  }, [q.talents.data, q.brands.data, q.deals.data]);

  // Frische Agentur (Inhaber 13.10.2026): fehlt eine Pflichtangabe, steht hier der Startleitfaden statt einer Sperrmeldung
  if (error && einrichtungsLuecke(error) && ws.isChef) return <StartStattUebersicht ws={ws} />;
  if (error) return <ErrorState error={error} retry={retry} />;
  // Probelauf H1: ohne Einstellungszeile (halb eingerichtete Agentur) nicht endlos laden, sondern zur Einrichtung führen
  if (!q.settings.isPending && q.settings.data === null) return ws.isChef ? <StartStattUebersicht ws={ws} /> : <EinrichtungOffen />;
  if (loading || !overview || !q.settings.data) return <Loading />;
  // Verständlichkeits-Lauf V-02: Ohne eine einzige Kooperation zeigten Kacheln und Filter nur Nullen und „Alles erledigt“ neben
  // dem offenen Leitfaden. Bis zur ersten Kooperation steht hier nur der Leitfaden (ausblendbar; dann die normale Übersicht).
  if (start.sichtbar && (q.deals.data ?? []).length === 0) return <StartStattUebersicht ws={ws} ausblendbar />;

  const settings = q.settings.data;
  const m = (c: number) => money(c, currency);
  const loc = numberLocale();
  // Ganze Beträge und Kurzbeträge über lib/format (de-CH mit ', wie formatMoney)
  const whole = (c: number) => moneyWhole(c, currency, loc);
  const short = (c: number) => shortAmount(c);
  const cur = overview.totals.currentMonth;
  // Anzeige in jeder Währung, die eingestellt ist oder in Kooperationen und Rechnungen vorkommt (wie die Kennzahlen)
  const currencies = [...new Set([settings.currency, ...(settings.currencies ?? []), ...(q.deals.data ?? []).map((d) => d.currency), ...(q.invoices.data ?? []).map((i) => i.currency)])];
  const direct = settings.invoice_route === "direct_to_brand";
  const hasData = (q.deals.data ?? []).length > 0 || (q.bookings.data ?? []).length > 0;
  const todos = agencyTodos({
    today, deals: q.deals.data!, periods: q.periods.data!, invoices: views,
    talentName: names.talent, brandName: names.brand,
    can: { dun: ws.can("dun"), send: ws.can("send_invoices"), approve: ws.can("approve_invoices"), confirm: ws.can("confirm_payments"), payout: ws.can("trigger_payouts") },
  });
  // Echte Lücken zuerst; Vertragshinweise als eine gebündelte Zeile (Befund 14)
  const allFindings = integrity?.findings ?? [];
  const vertragsHinweise = allFindings.filter((f) => f.kind === "billed_without_contract");
  const findings = allFindings.filter((f) => f.kind !== "billed_without_contract");
  const hinweise = findings.filter((f) => f.severity === "info").length + (vertragsHinweise.length ? 1 : 0) + (exklusiv.length ? 1 : 0);
  // „Offene Punkte“ zählt nur, was zu tun ist (Dringend und Prüfen), plus die nicht abgerechneten Monate; Hinweise stehen daneben
  const openPoints = findings.filter((f) => f.severity !== "info").length + (integrity?.notInvoicedPeriods ? 1 : 0);
  const katalog = q.koop.data?.platforms ?? [];
  const eigeneStatus = q.koop.data?.labels ?? [];
  const en = numberLocale().startsWith("en");
  const missing = setup.data?.missing ?? [];
  const missingSteps = [...new Set(missing.map((x) => x.step))];

  // Vergleich letzter voller Monat (AD-223)
  const lastFull = addMonths(cur, -1);
  const mgmtOf = (mm: string) => overview.occurrences.filter((o) => o.month === mm).reduce((a, o) => a + o.managementCents, 0);
  const lastFullV = mgmtOf(lastFull), beforeV = mgmtOf(addMonths(lastFull, -1)), lastYearV = mgmtOf(addMonths(lastFull, -12));
  const pct = (a: number, b: number) => (b > 0 ? ((a - b) / b) * 100 : null);

  // Monatsreihen (AD-022): Brutto, Netto (verbucht + vorläufig), vorläufig, verbucht, Kooperationsbudget, Agenturgebühren
  const months = overview.months;
  const series = [
    { key: "brutto", label: direct ? t("Vermitteltes Volumen netto", "Brokered volume net") : t("Gesamtvolumen netto", "Total volume net"), values: months.map((x) => x.grossCents), color: SERIES_COLORS[0]!, fill: true },
    { key: "netto", label: t("Netto Agenturumsatz", "Net agency revenue"), values: months.map((x) => x.managementCents), color: SERIES_COLORS[1]! },
    { key: "vorlaeufig", label: t("Netto voraussichtlich", "Net expected"), values: months.map((x) => x.provisionalCents), color: SERIES_COLORS[2]!, dashed: true },
    { key: "verbucht", label: t("Verbuchter Agenturumsatz", "Booked agency revenue"), values: months.map((x) => x.bookedCents), color: SERIES_COLORS[3]! },
    { key: "budget", label: t("Kooperationsbudget", "Deal budget"), values: months.map((x) => x.budgetCents), color: SERIES_COLORS[4]! },
    { key: "gebuehren", label: t("Agenturgebühren", "Agency fees"), values: months.map((x) => x.feeCents), color: SERIES_COLORS[5]! },
  ];
  const pipeline = overview.pipeline.map((p) => ({ ...p, ...PIPELINE_LABEL[p.key]!, statuses: PIPELINE_STAGES.find((s) => s.key === p.key)?.statuses ?? [] }));
  const pace = overview.talents
    .filter((t) => t.managementCents > 0 || t.previousYearManagementCents > 0)
    .map((t) => ({ ...t, delta: t.previousYearManagementCents > 0 ? ((t.managementCents - t.previousYearManagementCents) / t.previousYearManagementCents) * 100 : null }))
    .sort((a, b) => (b.delta ?? 999) - (a.delta ?? 999));
  // Nachfassen: letzte Kooperation liegt über drei Monate zurück und nichts ist geplant
  const followUp = (last: string | null, next: string | null) => !next && !!last && last < addMonths(cur, -3);

  return (
    <main id="main" className="content tk-uebersicht" ref={kompakt}>
      <div className="pagehead">
        <div>
          <h1>{t("Übersicht", "Overview")}</h1>
          <p>{t("Umsatz, Entwicklung und Creator-Performance.", "Revenue, development and creator performance.")}</p>
        </div>
        <div className="overview-filter">
          <label>
            <span className="tk-filter-name">{t("Währung", "Currency")}</span><select aria-label={t("Währung", "Currency")} value={currency} onChange={(e) => setCurrency(e.target.value)}>
              {currencies.map((c) => <option key={c} value={c}>{currencyOptionLabel(c, loc)}</option>)}
            </select>
          </label>
          <label>
            <span className="tk-filter-name">{t("Zeitraum", "Period")}</span><select aria-label={t("Zeitraum", "Period")} value={rangeKey} onChange={(e) => setRangeKey(e.target.value as RangeKey)}>
              {(["6m", "ytd", "12m", "prev"] as RangeKey[]).map((k) => <option key={k} value={k}>{rangeOf(k, today).label}</option>)}
              <option value="custom">{t("Eigener Zeitraum", "Custom period")}</option>
            </select>
          </label>
          {rangeKey === "custom" && (
            <>
              <label><span className="tk-filter-name">{t("Von", "From")}</span><input aria-label={t("Von", "From")} type="month" value={custom.from} max={custom.to} onChange={(e) => { const v = e.target.value; if (v) setCustom((c) => ({ from: v, to: c.to < v ? v : c.to })); }} /></label>
              <label><span className="tk-filter-name">{t("Bis", "Until")}</span><input aria-label={t("Bis", "Until")} type="month" value={custom.to} min={custom.from} onChange={(e) => { const v = e.target.value; if (v) setCustom((c) => ({ from: c.from > v ? v : c.from, to: v })); }} /></label>
            </>
          )}
          <label hidden={ws.isCreator}>
            <span className="tk-filter-name">Creator</span>
            <select aria-label="Creator" value={talentId} onChange={(e) => setTalentId(e.target.value)}>
              <option value="">{t("Alle Creator", "All creators")}</option>
              {/* Wie die Kennzahlen: archivierte Creator bleiben wählbar, gekennzeichnet (Befund 28) */}
              {(q.talents.data ?? []).map((tl) => <option key={tl.id} value={tl.id}>{talentName(tl)}{tl.status === "archived" ? t(" (archiviert)", " (archived)") : ""}</option>)}
            </select>
          </label>
          <small>{t(`Stand ${today.split("-").reverse().join(".")}`, `As of ${today.split("-").reverse().join("/")}`)}</small>
        </div>
      </div>

      <StartKarte ws={ws} />
      {/* Solange der Startleitfaden offen ist, führt sein erster Schritt zu den fehlenden Angaben; danach diese Liste */}
      {missingSteps.length > 0 && !start.sichtbar && (
        <section className="panel stack">
          <div className="row between">
            <h3>{t("Einrichtung abschließen", "Complete setup")}</h3>
            <small>{missing.length === 1 ? t("1 Pflichtangabe fehlt", "1 required detail missing") : t(`${missing.length} Pflichtangaben fehlen`, `${missing.length} required details missing`)} · {setupFolgen(missing.map((x) => x.key))}</small>
          </div>
          {missingSteps.map((s) => (
            <Link key={s} className="task" to={STEP_LINKS[s]?.to ?? "/agentur/einstellungen/firma"}>
              <div><strong>{STEP_LINKS[s]?.label ?? s}</strong><p>{(() => { const n = missing.filter((x) => x.step === s).length; return n === 1 ? t("1 Angabe fehlt", "1 detail missing") : t(`${n} Angaben fehlen`, `${n} details missing`); })()}</p></div>
              <span className="pill warning"><span className="pill-zeichen">◷</span> {t("Offen", "Open")}</span>
            </Link>
          ))}
        </section>
      )}

      {/* Tarifstaffel: ab 80 % Belegung von Creatorn, Team-Plätzen, Verträgen im Monat oder Speicher (nur Admins) */}
      {ws.isChef && <TarifGrenzenHinweis orgId={ws.org.org_id} />}
      <BeitraegeHinweis orgId={ws.org.org_id} today={today} />
      {ws.isChef && <EmpfehlungHinweis orgId={ws.org.org_id} today={today} />}

      <div className="integrity-banner">
        <p>
          <span className={`tk-punkt${openPoints ? " offen" : ""}`} aria-hidden="true" />
          {openPoints ? <strong>{openPoints === 1 ? t("1 offener Punkt", "1 open point") : t(`${openPoints} offene Punkte`, `${openPoints} open points`)}</strong> : <strong>{t("Alles erledigt", "All done")}</strong>}
          {integrity && integrity.notInvoicedCents > 0 ? t(` · ${m(integrity.notInvoicedCents)} nicht abgerechnet`, ` · ${m(integrity.notInvoicedCents)} not invoiced`) : openPoints ? "" : t(" · Rechnungen und Monate sind stimmig.", " · Invoices and months are consistent.")}
          {hinweise > 0 && <small> · {hinweise === 1 ? t("1 Hinweis", "1 notice") : t(`${hinweise} Hinweise`, `${hinweise} notices`)}</small>}
          {talentId && <small> {t("· nur ", "· only ")}{names.talent(talentId)}</small>}
        </p>
        <button className="btn" aria-expanded={detailsOpen} aria-controls="waechter" onClick={() => setDetailsOpen((o) => !o)}>{detailsOpen ? t("Schließen", "Close") : "Details"}</button>
      </div>
      {detailsOpen && (
        <section className="panel stack" id="waechter">
          <div className="row between">
            <h3>{t("Prüfungen", "Checks")}</h3>
            <small>
              {monthName(lastFull)}: {m(lastFullV)} {t("Agenturanteil", "Agency share")}{pct(lastFullV, beforeV) !== null && t(` · ${pct(lastFullV, beforeV)! >= 0 ? "+" : "−"}${percent(Math.abs(pct(lastFullV, beforeV)!))} zum ${monthName(addMonths(lastFull, -1))}`, ` · ${pct(lastFullV, beforeV)! >= 0 ? "+" : "−"}${percent(Math.abs(pct(lastFullV, beforeV)!))} compared with ${monthName(addMonths(lastFull, -1))}`)}
              {pct(lastFullV, lastYearV) !== null && t(` · ${pct(lastFullV, lastYearV)! >= 0 ? "+" : "−"}${percent(Math.abs(pct(lastFullV, lastYearV)!))} zum ${monthLabel(addMonths(lastFull, -12))}`, ` · ${pct(lastFullV, lastYearV)! >= 0 ? "+" : "−"}${percent(Math.abs(pct(lastFullV, lastYearV)!))} compared with ${monthLabel(addMonths(lastFull, -12))}`)}
            </small>
          </div>
          <div className="tk-findings">
            {findings.filter((f) => f.severity !== "info").map((f) => {
              const tl = findingText(f, { talent: names.talent, currency: names.currency }, currency);
              return (
                <Link key={f.key} className="task" to={findingHref(f)}>
                  <div><strong>{tl.title}</strong><p>{tl.detail}</p></div>
                  <span className={`pill ${f.severity === "error" ? "error" : f.severity === "warning" ? "warning" : "info"}`}>
                    {f.severity === "error" ? "!" : f.severity === "warning" ? "◷" : "◇"} {f.severity === "error" ? t("Dringend", "Urgent") : f.severity === "warning" ? t("Prüfen", "Check") : t("Hinweis", "Note")}
                  </span>
                </Link>
              );
            })}
            {integrity && integrity.notInvoicedPeriods > 0 && (
              <div className="task">
                <div>
                  <strong>{integrity.notInvoicedPeriods === 1 ? t("1 Monat", "1 month") : t(`${integrity.notInvoicedPeriods} Monate`, `${integrity.notInvoicedPeriods} months`)} {t("ohne Rechnung an die Marke", "without an invoice to the brand")}</strong>
                  <p>{m(integrity.notInvoicedCents)} {t("Agenturanteil noch nicht abgerechnet · abgeschlossene Monate und veröffentlichte Leistungen", "agency share not yet invoiced · completed months and published services")}</p>
                  <p className="row wrap">
                    {integrity.notInvoiced.slice(0, 12).map((x) => (
                      <Link key={x.periodId} to={`/agentur/kooperationen/${x.dealId}?monat=${x.month}`}>{names.deal(x.dealId)} · {monthLabel(x.month)}</Link>
                    ))}
                    {integrity.notInvoiced.length > 12 && <small>{t("und ", "and ")}{integrity.notInvoiced.length - 12} {t("weitere", "more")}</small>}
                  </p>
                </div>
                <span className="pill warning"><span className="pill-zeichen">◷</span> {t("Prüfen", "Check")}</span>
              </div>
            )}
            {openPoints === 0 && <p>{t("Für heute ist alles erledigt. Neue Befunde erscheinen hier automatisch.", "All done for today. New findings appear here automatically.")}</p>}
            {hinweise > 0 && <h4>{t("Hinweise", "Notices")}</h4>}
            {exklusiv.length > 0 && (
              <div className="task">
                <div>
                  <strong>{exklusiv.length === 1 ? t("1 Kooperation", "1 deal") : t(`${exklusiv.length} Kooperationen`, `${exklusiv.length} deals`)} {t("im Zeitraum einer Exklusivität", "during an exclusivity period")}</strong>
                  <p>{t("Gleicher Creator, andere Marke. Ob die Branche betroffen ist, entscheidet ihr.", "Same creator, different brand. You decide whether the industry is affected.")}</p>
                  <p className="row wrap">
                    {exklusiv.slice(0, 8).map((c) => (
                      <Link key={`${c.exclusiveDealId}:${c.otherDealId}`} to={`/agentur/kooperationen/${c.otherDealId}`}>
                        {names.talent(c.talentId)} · {names.deal(c.otherDealId)} {t("während ", "during ")}{names.deal(c.exclusiveDealId)}{c.scope ? ` (${c.scope})` : ""}
                      </Link>
                    ))}
                    {exklusiv.length > 8 && <small>{t("und ", "and ")}{exklusiv.length - 8} {t("weitere", "more")}</small>}
                  </p>
                </div>
                <span className={`pill ${exklusiv.some((c) => c.named) ? "warning" : "info"}`}><span className="pill-zeichen">{exklusiv.some((c) => c.named) ? "◷" : "◇"}</span> {exklusiv.some((c) => c.named) ? t("Prüfen", "Check") : t("Hinweis", "Notice")}</span>
              </div>
            )}
            {findings.filter((f) => f.severity === "info").map((f) => {
              const tl = findingText(f, { talent: names.talent, currency: names.currency }, currency);
              return (
                <Link key={f.key} className="task" to={findingHref(f)}>
                  <div><strong>{tl.title}</strong><p>{tl.detail}</p></div>
                  <span className="pill info"><span className="pill-zeichen">◇</span> {t("Hinweis", "Notice")}</span>
                </Link>
              );
            })}
            {vertragsHinweise.length > 0 && (
              <div className="task">
                <div>
                  <strong>{vertragsHinweise.length === 1 ? t("1 Kooperation", "1 deal") : t(`${vertragsHinweise.length} Kooperationen`, `${vertragsHinweise.length} deals`)} {t("abgerechnet ohne unterschriebenen Vertrag", "invoiced without a signed contract")}</strong>
                  <p>{t("Rechnungen der letzten ", "Invoices of the last ")}{CONTRACT_HINT_MONTHS} {t("Leistungsmonate · Vertrag anlegen oder verknüpfen", "service months · create or link a contract")}</p>
                  <button type="button" className="tk-linkbtn" aria-expanded={vertraegeOffen} onClick={() => setVertraegeOffen((o) => !o)}>
                    {vertraegeOffen ? t("Liste ausblenden", "Hide list") : t("Kooperationen zeigen", "Show deals")}
                  </button>
                  {vertraegeOffen && (
                    <p className="row wrap">
                      {vertragsHinweise.map((f) => <Link key={f.key} to={findingHref(f)}>{names.talent(f.talentId)} · {f.dealId ? names.deal(f.dealId) : "–"}</Link>)}
                    </p>
                  )}
                  {ws.isChef && (
                    <p className="muted">
                      {t("Arbeitet ihr ohne schriftliche Verträge (z. B. mit Mail-Zusagen)?", "Do you work without written contracts (e.g. with email confirmations)?")}{" "}
                      <button type="button" className="tk-linkbtn" onClick={() => void api.update("org_settings", [["org_id", "eq", ws.org.org_id]], { contract_warning: false })
                        .then(() => { toast(t("Hinweise zu fehlenden Verträgen sind abgeschaltet.", "Notices about missing contracts are switched off.")); void qc.invalidateQueries({ queryKey: ["org_settings", ws.org.org_id] }); })
                        .catch(() => toast(t("Das hat nicht geklappt.", "That did not work."), "alert"))}>{t("Hinweise zu fehlenden Verträgen abschalten", "Switch off notices about missing contracts")}</button>
                    </p>
                  )}
                </div>
                <span className="pill info"><span className="pill-zeichen">◇</span> {t("Hinweis", "Notice")}</span>
              </div>
            )}
          </div>
        </section>
      )}

      {/* Kompakt (D-1): was heute zu tun ist, steht als Erstes; eine Zeile je Aufgabe, ab 1100 px zweispaltig */}
      <section className="panel stack tk-heute" id="heute">
        <div className="row between">
          <h3>{t("Was heute zu tun ist", "What to do today")}</h3>
          {todos.length > 0 && <small className="num">{todos.length === 1 ? t("1 Aufgabe", "1 task") : t(`${todos.length} Aufgaben`, `${todos.length} tasks`)}</small>}
        </div>
        {todos.length > 0 && (
          <ul className="tk-heute-liste">
            {(alleAufgaben ? todos : todos.slice(0, 12)).map((x) => (
              <li key={x.key}>
                <Link to={x.to}>
                  <span className={`tk-heute-ton ${x.tone}`} aria-hidden="true" />
                  <span className="tk-ell"><strong>{x.title}</strong> <span className="tk-heute-detail">· {x.detail}</span></span>
                  <small className="tk-heute-frist">{dueLabel(x.due, today)}</small>
                </Link>
              </li>
            ))}
          </ul>
        )}
        {todos.length > 12 && (
          <button type="button" className="tk-linkbtn" aria-expanded={alleAufgaben} onClick={() => setAlleAufgaben((o) => !o)}>
            {alleAufgaben ? t("Weniger zeigen", "Show fewer") : t(`Alle ${todos.length} zeigen`, `Show all ${todos.length}`)}
          </button>
        )}
        {todos.length === 0 && <p>{t("Für heute ist alles erledigt. Neue Aufgaben erscheinen hier automatisch.", "All done for today. New tasks appear here automatically.")}</p>}
      </section>

      {!hasData && (
        <section className="panel stack tk-empty">
          <h3>{t("Hier beginnt dein nächster Deal", "Your next deal starts here")}</h3>
          <p>{t("Sobald die erste Kooperation angelegt ist, erscheinen hier Umsatz, Pipeline, offene Rechnungen und die Entwicklung deiner Creator.", "As soon as the first deal is created, revenue, pipeline, open invoices and the development of your creators appear here.")}</p>
          <div className="row wrap">
            {/* Nur-Lesen (Test-Chat 04.11.2026): Anlegen entfällt ausdrücklich, nicht nur über die Regel in portal.css */}
            <NurSchreiben><Link className="btn primary" to="/agentur/kooperationen/neu">{t("Kooperation anlegen", "Create deal")}</Link></NurSchreiben>
            <NurSchreiben><Link className="btn" to="/agentur/creator">{t("Creator anlegen", "Create creator")}</Link></NurSchreiben>
            <NurSchreiben><Link className="btn" to="/agentur/marken">{t("Marken anlegen", "Create brands")}</Link></NurSchreiben>
          </div>
        </section>
      )}

      {hasData && <>
      <section>
        <div className="group-label">{t("Zeitraum-unabhängig", "Independent of period")}</div>
        <div className="overview-kpis total">
          <div className="kpi">
            <small>{t("Agenturanteil · offen", "Agency share · open")}</small>
            <KachelBetrag cents={overview.totals.openManagementCents} currency={currency} />
            <small className="tk-ell">{t("Aus allen offenen Kooperationen · netto", "From all open deals · net")}</small>
            {overview.totals.requestedWithoutInvoice > 0 && (
              <div className="kpi-alert">{overview.totals.requestedWithoutInvoice === 1 ? t("1 angeforderter Monat", "1 requested month") : t(`${overview.totals.requestedWithoutInvoice} angeforderte Monate`, `${overview.totals.requestedWithoutInvoice} requested months`)} {t("ohne Rechnung · Abrechnung prüfen", "without invoice · check billing")}</div>
            )}
          </div>
          <div className="kpi">
            <small>{t("Ø Agenturanteil / Monat", "Ø agency share / month")}</small>
            <KachelBetrag cents={overview.totals.runRateMonths ? overview.totals.runRateCents : null} currency={currency} />
            <small className="tk-ell">{overview.totals.runRateMonths === 0 ? t("Noch kein voller Monat", "No full month yet") : overview.totals.runRateMonths === 12 ? t("Letzte 12 Monate · netto", "Last 12 months · net") : t(`Seit Beginn, ${overview.totals.runRateMonths === 1 ? t("1 voller Monat", "1 full month") : t(`${overview.totals.runRateMonths} volle Monate`, `${overview.totals.runRateMonths} full months`)} · netto`, `Since the start, ${overview.totals.runRateMonths === 1 ? t("1 voller Monat", "1 full month") : t(`${overview.totals.runRateMonths} volle Monate`, `${overview.totals.runRateMonths} full months`)} · net`)}</small>
          </div>
          <div className="kpi">
            <small>{t("Aktueller Monat ", "Current month ")}<span className="kpi-chip">{monthName(cur)}</span></small>
            <KachelBetrag cents={overview.totals.currentMonthManagementCents} currency={currency} />
            {/* K-154 a: ohne Wert im Vormonat kein leerer Vergleich */}
            <small className="tk-ell">{overview.totals.monthDeltaPercent === null || !Number.isFinite(overview.totals.monthDeltaPercent)
              ? t("Kein Vergleich: im Vormonat kein Umsatz", "No comparison: no revenue in the previous month")
              : <>{t("Zum Vormonat ", "Compared with previous month ")}<Delta value={overview.totals.monthDeltaPercent} /></>}</small>
          </div>
          <div className="kpi">
            <small>{t("Wiederbuchungsrate ", "Rebooking rate ")}<span className="kpi-chip">{t("gesamt", "total")}</span></small>
            <div className="metric num">{overview.totals.brandCount ? percent(overview.totals.rebookRatePercent) : "–"}</div>
            <small className="tk-ell">{overview.totals.brandCount
              ? <>{overview.totals.rebookers} {t("von ", "of ")}{overview.totals.brandCount} {t("Marken erneut gebucht", "brands booked again")}</>
              : t("Noch keine Marke gebucht", "No brand booked yet")}</small>
          </div>
        </div>
      </section>

      <section>
        <div className="group-label">{t("Gewählter Zeitraum · ", "Selected period · ")}{r.label}</div>
        <div className="overview-kpis period">
          <div className="kpi">
            <small>{t("Kooperationsbudget", "Deal budget")}</small>
            <KachelBetrag cents={overview.period.budgetCents} currency={currency} />
            <small className="tk-ell">{m(overview.period.budgetBookedCents)} {t("verbucht · ", "booked · ")}{m(overview.period.budgetProvisionalCents)} {t("vorläufig", "provisional")}</small>
          </div>
          <div className="kpi">
            <small>{t("Agenturgebühren", "Agency fees")}</small>
            <KachelBetrag cents={overview.period.feeCents} currency={currency} />
            <small className="tk-ell">{m(overview.period.feeBookedCents)} {t("verbucht · ", "booked · ")}{m(overview.period.feeOpenCents)} {t("offen", "open")}</small>
          </div>
          <div className="kpi">
            <small>{direct ? t("Vermitteltes Volumen · netto", "Brokered volume · net") : t("Gesamtvolumen · netto", "Total volume · net")}</small>
            <KachelBetrag cents={overview.period.grossCents} currency={currency} />
            <small className="tk-ell">{t("Kooperationen, Zusatz und Gebühren vor Aufteilung, ohne ", "Deals, extras and fees before split, excluding ")}{vatWord(ws.org.country)}</small>
          </div>
          <div className="kpi">
            <small>{t("Rechnungsvolumen · brutto", "Invoice volume · gross")}</small>
            <KachelBetrag cents={overview.period.invoiceGrossCents} currency={currency} />
            <small className="tk-ell">{t("Rechnungen an Marken nach Leistungsmonat, inklusive ", "Invoices to brands by service month, including ")}{vatWord(ws.org.country)}</small>
          </div>
          <div className="kpi">
            <small>{t("Vorjahr · gleiche Monate", "Previous year · same months")}</small>
            <KachelBetrag cents={overview.totals.previousYearKnown ? overview.period.previousYearManagementCents : null} currency={currency} />
            <small className="tk-ell">{overview.totals.previousYearKnown ? <>{t("Agenturanteil ", "Agency share ")}{monthLabel(addMonths(r.from, -12))} – {monthLabel(addMonths(r.to, -12))} <Delta value={overview.totals.yearDeltaPercent} /></> : t("Noch kein Vergleich: im Vorjahr gab es keine Umsätze", "No comparison yet: there was no revenue in the previous year")}</small>
          </div>
          {overview.period.referralCents > 0 && (
            <div className="kpi">
              <small>{t("Vermittlerprovisionen", "Referral commissions")}</small>
              <KachelBetrag cents={overview.period.referralCents} currency={currency} />
              <small className="tk-ell">{t("Aufwand an Partner und Scouts · Agentur netto danach ", "Expenses for partners and scouts · agency net afterwards ")}{m(overview.period.managementCents - overview.period.referralCents)}</small>
            </div>
          )}
          <div className="kpi">
            <small>{t("Ø Kooperationswert", "Ø deal value")}</small>
            <KachelBetrag cents={overview.period.avgDealCents} currency={currency} />
            <small className="tk-ell">{t("Aus ", "From ")}{overview.period.dealCount === 1 ? t("1 Kooperation", "1 deal") : t(`${overview.period.dealCount} Kooperationen`, `${overview.period.dealCount} deals`)} {t("im Zeitraum", "in the period")}</small>
          </div>
        </div>
      </section>

      <section className="panel">
        <div className="row between">
          <h3>{t("Monatsumsatz", "Monthly revenue")}</h3>
          <small>{t("Klick auf einen Monat öffnet die Details · Klick in der Legende hebt Linien hervor", "Click a month to open the details · click the legend to highlight lines")}</small>
        </div>
        <LineChart
          labels={months.map((x) => monthShort(x.month))}
          series={series}
          initialActive={["brutto", "netto", "vorlaeufig"]}
          format={m}
          short={short}
          current={months.findIndex((x) => x.month === cur)}
          onPick={(i) => setMonth(months[i]?.month ?? null)}
          caption={t(`Monatsumsatz ${r.label}`, `Monthly revenue ${r.label}`)}
          height={240}
        />
      </section>

      <div className="grid tk-chart-grid">
        <section className="panel stack">
          <div className="row between"><h3>{t("Top-Creator · Agenturumsatz netto", "Top creators · agency revenue net")}</h3><small>{r.label}</small></div>
          <RankBars rows={overview.topTalents.slice(0, 8).map((t) => ({ key: t.talentId, label: names.talent(t.talentId), to: `/agentur/creator/${t.talentId}`, value: t.managementCents }))}
            format={whole} empty={t("Im gewählten Zeitraum gibt es keine Umsätze.", "There is no revenue in the selected period.")} />
        </section>
        <section className="panel stack">
          <div className="row between"><h3>{t("Top-Marken · Agenturumsatz netto", "Top brands · agency revenue net")}</h3><small>{r.label}</small></div>
          <RankBars rows={overview.topBrands.slice(0, 8).map((b) => ({ key: b.brandId, label: names.brand(b.brandId), to: `/agentur/marken/${b.brandId}`, value: b.managementCents }))}
            format={whole} empty={t("Im gewählten Zeitraum gibt es keine Umsätze.", "There is no revenue in the selected period.")} />
        </section>
      </div>

      <div className="grid tk-chart-grid">
        <section className="panel stack">
          <div className="row between"><h3>{t("Umsatz nach Plattform", "Revenue by platform")}</h3><small>{r.label}</small></div>
          <Donut segments={overview.platforms.map((p) => ({ label: platformName(p.platform, katalog, en), value: p.managementCents }))}
            colors={SERIES_COLORS} format={whole} caption={t("Agenturumsatz nach Plattform", "Agency revenue by platform")} />
        </section>
        <section className="panel stack">
          <div className="row between"><h3>{t("Kooperations-Pipeline", "Deal pipeline")}</h3><small>{t("Laufend · noch nicht abgerechnete Monate · netto", "Ongoing · months not yet invoiced · net")}</small></div>
          <BarChart items={pipeline.map((p) => ({ label: `${p.label} · ${p.deals}`, value: p.budgetCents,
            hint: `${p.statuses.map((x) => statusLabel(x, eigeneStatus, en)).join(", ")}${p.noMoneyDeals ? t(` · davon ${p.noMoneyDeals} ohne Geldbetrag`, ` · of which ${p.noMoneyDeals} without an amount`) : ""}` }))}
            tones={pipeline.map((p) => p.tone)} format={whole} caption={t("Offenes Kooperationsbudget nach Stufe", "Open deal budget by stage")} />
        </section>
      </div>

      <div className="grid tk-chart-grid">
        <section className="panel stack">
          <div className="row between"><h3>{t("Offene Rechnungen nach Fälligkeit", "Open invoices by due date")}</h3><small>{t("Versendet, an Marken und Creator · brutto", "Sent, to brands and creators · gross")}</small></div>
          <BarChart items={overview.aging.map((a) => ({ label: `${AGING_LABEL[a.bucket]} · ${a.count}`, value: a.cents }))}
            tones={["neutral", "warning", "warning", "error", "error"]} format={whole} caption={t("Offene Rechnungen nach Fälligkeit", "Open invoices by due date")} />
          {overview.toSend.count > 0 && (
            <Link to="/agentur/rechnungen?filter=offen">
              {overview.toSend.count === 1 ? t("1 freigegebene Rechnung", "1 approved invoice") : t(`${overview.toSend.count} freigegebene Rechnungen`, `${overview.toSend.count} approved invoices`)} {t("über ", "over ")}{m(overview.toSend.cents)} {t("noch nicht versendet", "not yet sent")}</Link>
          )}
          {overview.creditNotesToSend > 0 && (
            <Link to="/agentur/rechnungen?filter=offen">
              {overview.creditNotesToSend === 1 ? t("1 Stornorechnung noch nicht versendet", "1 cancellation invoice not yet sent") : t(`${overview.creditNotesToSend} Stornorechnungen noch nicht versendet`, `${overview.creditNotesToSend} cancellation invoices not yet sent`)}</Link>
          )}
        </section>
        <section className="panel stack">
          <div className="row between"><h3>{t("Umsatzvorschau", "Revenue forecast")}</h3><small>{t("Nächste sechs Monate · nach Leistungsmonat · netto", "Next six months · by service month · net")}</small></div>
          <LineChart
            labels={overview.cashflow.map((c) => monthShort(c.month))}
            series={[
              { key: "brutto", label: direct ? t("Vermitteltes Volumen", "Brokered volume") : t("Gesamtvolumen", "Total volume"), values: overview.cashflow.map((c) => c.grossCents), color: SERIES_COLORS[0]!, fill: true },
              { key: "netto", label: t("Agenturanteil voraussichtlich", "Expected agency share"), values: overview.cashflow.map((c) => c.managementCents), color: SERIES_COLORS[1]!, dashed: true },
            ]}
            format={m} short={short} caption={t("Umsatzvorschau der nächsten sechs Monate", "Revenue forecast for the next six months")} height={240} compact
          />
        </section>
      </div>

      <div className="grid tk-chart-grid">
        <section className="panel stack">
          <div className="row between"><h3>{t("Umsatzentwicklung zum Vorjahr", "Revenue development compared with the previous year")}</h3><small>{t("Wer läuft im Zeitraum stärker oder schwächer", "Who is doing better or worse in the period")}</small></div>
          <RankBars
            rows={pace.slice(0, 8).map((tl) => ({
              key: tl.talentId, label: names.talent(tl.talentId), to: `/agentur/creator/${tl.talentId}`, value: tl.managementCents,
              note: tl.delta === null ? <span className="tk-delta flat">{t("neu", "new")}</span> : <span className={`tk-delta ${tl.delta >= 0 ? "up" : "down"}`}>{tl.delta >= 0 ? "+" : "−"}{percent(Math.abs(tl.delta))}</span>,
            }))}
            format={whole} empty={t("Noch keine Vergleichswerte.", "No comparison values yet.")}
          />
        </section>
        <section className="panel stack tk-compact tk-perf">
          <div className="row between"><h3>{t("Creator-Performance", "Creator performance")}</h3><small>{t("Alle Creator im Zeitraum", "All creators in the period")}</small></div>
          {/* N-1: in der halben Karte (bei 1024 px) fallen „Koop.“ und „Zuletzt“ weg und stehen unter dem Namen; N-4: am Handy Karten */}
          <div className="tablewrap tk-rahmen tk-cardtable tk-handykarten">
            <table>
              <caption className="sr-only">{t("Creator-Performance", "Creator performance")}</caption>
              <thead>
                <tr><th scope="col">Creator</th><th scope="col" className="money tk-r-weg-s"><abbr title={t("Kooperationen", "Deals")}>{t("Koop.", "Deals")}</abbr></th><th scope="col" className="money">{t("Umsatz", "Revenue")}</th><th scope="col" className="money"><abbr title={t("Agenturanteil", "Agency share")}>{t("Anteil", "Share")}</abbr></th><th scope="col" className="tk-r-weg-s">{t("Zuletzt", "Last")}</th></tr>
              </thead>
              <tbody>
                {overview.talents.map((x) => {
                  const zuletzt = x.lastMonth ? `${monthShort(x.lastMonth)} ${x.lastMonth.slice(0, 4)}` : "–";
                  return (
                  <tr key={x.talentId}>
                    <td className="tk-karte-titel"><span className="tk-ell"><Link to={`/agentur/creator/${x.talentId}`}>{names.talent(x.talentId)}</Link></span>
                      <small className="tk-r-alt-s">{x.deals} {t("Koop.", "deals")} · {zuletzt}</small></td>
                    <td className="tk-r-weg-s" data-label={t("Kooperationen", "Deals")}><span className="num">{x.deals}</span></td>
                    <td data-label={t("Umsatz", "Revenue")}><span className="num">{whole(x.grossCents)}</span></td>
                    <td data-label={t("Agenturanteil", "Agency share")}><span className="num">{whole(x.managementCents)}</span></td>
                    <td className="tk-r-weg-s" data-label={t("Zuletzt", "Last")}>{zuletzt}</td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </section>
      </div>

      <section className="panel stack">
        <div className="row between"><h3>{t("Marken-Übersicht · Lifetime Value", "Brand overview · lifetime value")}</h3><small>{t("Alle Kooperationen bis heute · Klick öffnet das Profil", "All deals to date · click opens the profile")}</small></div>
        <div className="tablewrap tk-marken-ltv tk-cardtable tk-handykarten">
          <table>
            <caption className="sr-only">{t("Marken nach Lifetime Value", "Brands by lifetime value")}</caption>
            <thead>
              <tr>
                <th scope="col">{t("Marke", "Brand")}</th><th scope="col" className="money"><abbr title={t("Kooperationen", "Deals")}>{t("Koop.", "Deals")}</abbr></th><th scope="col" className="money">Creator</th>
                <th scope="col" className="money">{t("Umsatz gesamt", "Total revenue")}</th><th scope="col" className="money">{t("Agenturanteil gesamt", "Total agency share")}</th>
                <th scope="col" className="money">{t("Im Zeitraum", "In the period")}</th><th scope="col">{t("Letzte Kooperation", "Last deal")}</th>
              </tr>
            </thead>
            <tbody>
              {overview.brandsLifetime.map((b) => (
                <tr key={b.brandId}>
                  <td className="tk-karte-titel"><strong className="tk-ell"><Link to={`/agentur/marken/${b.brandId}`}>{names.brand(b.brandId)}</Link></strong></td>
                  <td data-label={t("Kooperationen", "Deals")}><span className="num">{b.deals}</span></td>
                  <td data-label="Creator"><span className="num">{b.talents}</span></td>
                  <td data-label={t("Umsatz gesamt", "Total revenue")}><span className="num">{whole(b.grossCents)}</span></td>
                  <td data-label={t("Agenturanteil gesamt", "Total agency share")}><span className="num">{whole(b.managementCents)}</span></td>
                  <td data-label={t("Im Zeitraum", "In the period")}><span className="num">{whole(b.rangeManagementCents)}</span></td>
                  <td data-label={t("Letzte Kooperation", "Last deal")}>
                    {b.lastMonth ? `${monthShort(b.lastMonth)} ${b.lastMonth.slice(0, 4)}` : "–"}
                    {b.nextMonth && <small> {t("· geplant ab ", "· planned from ")}{`${monthShort(b.nextMonth)} ${b.nextMonth.slice(0, 4)}`}</small>}
                    {followUp(b.lastMonth, b.nextMonth) && <small><span className="pill warning"><span className="pill-zeichen">◷</span> {t("Nachfassen", "Follow up")}</span></small>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      </>}

      {overview.unconverted > 0 && <p className="demo-note">{overview.unconverted === 1 ? t("1 Posten in anderer Währung ohne hinterlegten Kurs ist nicht enthalten.", "1 item in another currency without an exchange rate is not included.")
        : t(`${overview.unconverted} Posten in anderer Währung ohne hinterlegten Kurs sind nicht enthalten.`, `${overview.unconverted} items in another currency without an exchange rate are not included.`)}</p>}
      {api.mode === "demo" && <p className="demo-note">{t("Demo · Alle Daten sind erfunden.", "Demo · All data is fictional.")}</p>}

      <MonatsDetails month={month} occurrences={overview.occurrences} currency={currency} onClose={() => setMonth(null)}
        dealTitle={names.deal} talentName={(id) => names.talent(id)} brandName={(id) => names.brand(id)}
        dealModel={(id) => (q.deals.data ?? []).find((d) => d.id === id)?.model ?? null} />
    </main>
  );
}
